import ColorThief from 'colorthief';

export interface ExtractedColorPalette {
  dominantColor: string;
  palette: string[];
  suggestedPrimary: string;
  suggestedSecondary: string;
  isLight: boolean;
}

/**
 * Convert RGB array to hex color string
 */
function rgbToHex(rgb: number[]): string {
  return '#' + rgb.map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Calculate relative luminance to determine if color is light or dark
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(rgb: number[]): number {
  const [r, g, b] = rgb.map(val => {
    const normalized = val / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Determine if a color is light based on luminance
 */
function isLightColor(rgb: number[]): boolean {
  return getRelativeLuminance(rgb) > 0.5;
}

/**
 * Calculate color distance using Delta E formula (simplified)
 */
function colorDistance(rgb1: number[], rgb2: number[]): number {
  return Math.sqrt(
    Math.pow(rgb1[0] - rgb2[0], 2) +
    Math.pow(rgb1[1] - rgb2[1], 2) +
    Math.pow(rgb1[2] - rgb2[2], 2)
  );
}

/**
 * Find the most vibrant color from palette
 */
function findMostVibrantColor(palette: number[][]): number[] {
  let mostVibrant = palette[0];
  let maxSaturation = 0;

  palette.forEach(rgb => {
    const r = rgb[0] / 255;
    const g = rgb[1] / 255;
    const b = rgb[2] / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;
    
    if (saturation > maxSaturation) {
      maxSaturation = saturation;
      mostVibrant = rgb;
    }
  });

  return mostVibrant;
}

/**
 * Extract color palette from an image URL
 */
export async function extractColorPalette(imageUrl: string): Promise<ExtractedColorPalette> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const colorThief = new ColorThief();
        
        // Get dominant color
        const dominantRgb = colorThief.getColor(img);
        const dominantHex = rgbToHex(dominantRgb);
        
        // Get color palette (8 colors)
        const paletteRgb = colorThief.getPalette(img, 8);
        const paletteHex = paletteRgb.map(rgbToHex);
        
        // Find most vibrant color for primary
        const vibrantRgb = findMostVibrantColor(paletteRgb);
        const suggestedPrimary = rgbToHex(vibrantRgb);
        
        // Find a complementary color for secondary
        // Look for a color that's different enough from primary
        let suggestedSecondary = dominantHex;
        let maxDistance = 0;
        
        paletteRgb.forEach(rgb => {
          const distance = colorDistance(vibrantRgb, rgb);
          if (distance > maxDistance && distance > 100) {
            maxDistance = distance;
            suggestedSecondary = rgbToHex(rgb);
          }
        });
        
        resolve({
          dominantColor: dominantHex,
          palette: paletteHex,
          suggestedPrimary,
          suggestedSecondary,
          isLight: isLightColor(dominantRgb)
        });
      } catch (error) {
        reject(new Error('Failed to extract colors from image'));
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
}

/**
 * Process an image file and extract colors
 */
export async function extractColorPaletteFromFile(file: File): Promise<ExtractedColorPalette> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      extractColorPalette(dataUrl).then(resolve).catch(reject);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * Suggest text colors based on background color
 */
export function suggestTextColors(backgroundColor: string): {
  primaryText: string;
  secondaryText: string;
} {
  // Convert hex to RGB
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const isLight = isLightColor([r, g, b]);
  
  return {
    primaryText: isLight ? '#000000' : '#FFFFFF',
    secondaryText: isLight ? '#333333' : '#E5E5E5'
  };
}